import React from "react";
import {
  Box,
  FormControl,
  FormHelperText,
  FormLabel,
  Input,
  Option,
  Select,
  Sheet,
  Stack,
  Switch,
  Textarea,
  Typography,
} from "@mui/joy";
import {
  JsonSchemaObject,
  JsonSchemaProperty,
} from "../types/integrationCatalog";
import {
  getIntegrationWidget,
  IntegrationWidgetContext,
} from "./IntegrationWidgets";

interface JsonSchemaFormProps {
  schema?: JsonSchemaObject | null;
  value: Record<string, any>;
  onChange: (nextValue: Record<string, any>) => void;
  secretFields?: string[];
  storedSecretFields?: string[];
  disabled?: boolean;
  idPrefix?: string;
  widgetContext?: IntegrationWidgetContext;
}

const humanizeKey = (key: string) =>
  key.replace(/[_-]+/g, " ").replace(/\b\w/g, (match) => match.toUpperCase());

const primaryType = (property: JsonSchemaProperty): string => {
  if (Array.isArray(property.type)) {
    return property.type.find((type) => type !== "null") || property.type[0];
  }
  return property.type || (property.properties ? "object" : "string");
};

const readPath = (value: Record<string, any>, path: string[]) =>
  path.reduce<any>((acc, key) => {
    if (acc && typeof acc === "object") return acc[key];
    return undefined;
  }, value);

const writePath = (
  value: Record<string, any>,
  path: string[],
  nextFieldValue: any,
): Record<string, any> => {
  if (path.length === 0) return value;

  const [head, ...tail] = path;
  if (tail.length === 0) {
    return { ...value, [head]: nextFieldValue };
  }

  const current = value[head];
  return {
    ...value,
    [head]: writePath(
      current && typeof current === "object" && !Array.isArray(current)
        ? current
        : {},
      tail,
      nextFieldValue,
    ),
  };
};

const arrayToText = (fieldValue: any) => {
  if (Array.isArray(fieldValue)) return fieldValue.join("\n");
  if (typeof fieldValue === "string") return fieldValue;
  return "";
};

const textToArray = (fieldValue: string) =>
  fieldValue
    .split("\n")
    .map((item) => item.trim())
    .filter(Boolean);

const normalizeNumber = (fieldValue: string, type: string) => {
  if (fieldValue === "") return undefined;
  return type === "integer" ? parseInt(fieldValue, 10) : Number(fieldValue);
};

interface FieldRendererProps {
  name: string;
  property: JsonSchemaProperty;
  path: string[];
  value: Record<string, any>;
  onChange: (nextValue: Record<string, any>) => void;
  required: boolean;
  secretFields: Set<string>;
  storedSecretFields: Set<string>;
  disabled?: boolean;
  idPrefix: string;
  widgetContext?: IntegrationWidgetContext;
}

const FieldRenderer: React.FC<FieldRendererProps> = ({
  name,
  property,
  path,
  value,
  onChange,
  required,
  secretFields,
  storedSecretFields,
  disabled,
  idPrefix,
  widgetContext,
}) => {
  const type = primaryType(property);
  const label = property.title || humanizeKey(name);
  const fieldValue = readPath(value, path);
  const pathKey = path.join(".");
  const fieldId = `${idPrefix}-${path.join("-")}`;
  const isSecret =
    secretFields.has(name) ||
    secretFields.has(pathKey) ||
    property.format === "password" ||
    property.format === "secret";
  const isStoredSecret =
    storedSecretFields.has(name) || storedSecretFields.has(pathKey);
  const showStoredSecret =
    isStoredSecret && (fieldValue === undefined || fieldValue === "");

  const setFieldValue = (nextFieldValue: any) => {
    onChange(writePath(value, path, nextFieldValue));
  };

  const WidgetComponent = getIntegrationWidget(property["x-widget"]);

  if (type === "object" && property.properties) {
    const nestedRequired = new Set(property.required || []);
    return (
      <Sheet
        variant="outlined"
        sx={{
          p: 2,
          borderRadius: "8px",
          display: "grid",
          gap: 1.5,
        }}
      >
        <Box>
          <Typography level="title-sm">{label}</Typography>
          {property.description && (
            <Typography level="body-sm" color="neutral">
              {property.description}
            </Typography>
          )}
        </Box>
        {Object.entries(property.properties).map(([nestedName, nestedProp]) => (
          <FieldRenderer
            key={[...path, nestedName].join(".")}
            name={nestedName}
            property={nestedProp}
            path={[...path, nestedName]}
            value={value}
            onChange={onChange}
            required={nestedRequired.has(nestedName)}
            secretFields={secretFields}
            storedSecretFields={storedSecretFields}
            disabled={disabled}
            idPrefix={idPrefix}
            widgetContext={widgetContext}
          />
        ))}
      </Sheet>
    );
  }

  if (WidgetComponent) {
    return (
      <FormControl required={required} disabled={disabled}>
        <FormLabel htmlFor={fieldId}>{label}</FormLabel>
        <WidgetComponent
          schema={property as JsonSchemaObject}
          value={value}
          onChange={onChange}
          disabled={disabled}
          fieldId={fieldId}
          context={widgetContext}
        />
        {property.description && (
          <FormHelperText>{property.description}</FormHelperText>
        )}
      </FormControl>
    );
  }

  return (
    <FormControl required={required} disabled={disabled}>
      <FormLabel htmlFor={fieldId}>{label}</FormLabel>
      {property.enum ? (
        <Select
          value={fieldValue ?? ""}
          onChange={(_, nextValue) => setFieldValue(nextValue)}
          slotProps={{
            button: {
              id: fieldId,
              "aria-label": label,
            },
          }}
        >
          {property.enum.map((option) => (
            <Option key={String(option)} value={option as any}>
              {String(option)}
            </Option>
          ))}
        </Select>
      ) : type === "boolean" ? (
        <Switch
          id={fieldId}
          checked={Boolean(fieldValue)}
          onChange={(event) => setFieldValue(event.target.checked)}
        />
      ) : type === "array" ? (
        <Textarea
          minRows={3}
          value={arrayToText(fieldValue)}
          onChange={(event) => setFieldValue(textToArray(event.target.value))}
          placeholder="One value per line"
          slotProps={{ textarea: { id: fieldId } }}
        />
      ) : (
        <Input
          type={
            isSecret
              ? "password"
              : type === "integer" || type === "number"
                ? "number"
                : "text"
          }
          value={fieldValue ?? ""}
          onChange={(event) => {
            const nextValue = event.target.value;
            setFieldValue(
              type === "integer" || type === "number"
                ? normalizeNumber(nextValue, type)
                : nextValue,
            );
          }}
          slotProps={{ input: { id: fieldId } }}
        />
      )}
      {(showStoredSecret || property.description) && (
        <FormHelperText>
          {showStoredSecret
            ? "Stored. Leave blank to keep the current value."
            : property.description}
        </FormHelperText>
      )}
    </FormControl>
  );
};

const JsonSchemaForm: React.FC<JsonSchemaFormProps> = ({
  schema,
  value,
  onChange,
  secretFields = [],
  storedSecretFields = [],
  disabled = false,
  idPrefix = "json-schema-form",
  widgetContext,
}) => {
  const properties = schema?.properties || {};
  const required = new Set(schema?.required || []);
  const secretFieldSet = new Set(secretFields);
  const storedSecretFieldSet = new Set(storedSecretFields);
  const RootWidget = getIntegrationWidget(schema?.["x-widget"]);

  if (RootWidget && schema) {
    return (
      <RootWidget
        schema={schema}
        value={value}
        onChange={onChange}
        disabled={disabled}
        fieldId={idPrefix}
        context={widgetContext}
      />
    );
  }

  if (!schema || Object.keys(properties).length === 0) {
    return (
      <Typography level="body-sm" color="neutral">
        No configuration fields are required.
      </Typography>
    );
  }

  return (
    <Stack spacing={1.5}>
      {Object.entries(properties).map(([name, property]) => (
        <FieldRenderer
          key={name}
          name={name}
          property={property}
          path={[name]}
          value={value}
          onChange={onChange}
          required={required.has(name)}
          secretFields={secretFieldSet}
          storedSecretFields={storedSecretFieldSet}
          disabled={disabled}
          idPrefix={idPrefix}
          widgetContext={widgetContext}
        />
      ))}
    </Stack>
  );
};

export default JsonSchemaForm;
export { humanizeKey };
